import { compactData, stringList, stringValue } from "./firestore";

// Port of LeagueAthleteFieldDefinition + LeagueCompetitionUniversalFieldRole
// (Models/LeagueAthleteSchemaModel.swift, Models/LeagueCompetitionTemplateModel.swift).

export type LeagueAthleteFieldType =
  | "text"
  | "integer"
  | "decimal"
  | "boolean"
  | "date"
  | "dateTime"
  | "singleSelect"
  | "multiSelect"
  | "email"
  | "url"
  | "phone"
  | "photo";

export const FIELD_TYPES: LeagueAthleteFieldType[] = [
  "text",
  "integer",
  "decimal",
  "boolean",
  "date",
  "dateTime",
  "singleSelect",
  "multiSelect",
  "email",
  "url",
  "phone",
  "photo",
];

export const FIELD_TYPE_LABELS: Record<LeagueAthleteFieldType, string> = {
  text: "Text",
  integer: "Integer",
  decimal: "Decimal",
  boolean: "Yes/No",
  date: "Date",
  dateTime: "Date & Time",
  singleSelect: "Single select",
  multiSelect: "Multi select",
  email: "Email",
  url: "URL",
  phone: "Phone",
  photo: "Photo",
};

export const ALLOWED_PHOTO_MIME_TYPES = ["image/jpeg", "image/png", "image/heic"];

export type LeagueAthleteFieldPrivacyScope = "public" | "organization" | "league";

export interface LeagueAthleteFieldOption {
  id: string;
  label: string;
}

export interface LeagueAthleteFieldValidation {
  minNumber?: number;
  maxNumber?: number;
  minLength?: number;
  maxLength?: number;
  minSelections?: number;
  maxSelections?: number;
  allowedMIMETypes: string[];
}

export type LeagueCompetitionUniversalFieldRole =
  | "eventName"
  | "eventCode"
  | "eventShortName"
  | "eventStartDateTime"
  | "eventLogo"
  | "eventLogoDark"
  | "eventTitleSponsor"
  | "eventCoverImage"
  | "eventVenueName"
  | "eventAddress"
  | "eventCity"
  | "eventRegion"
  | "eventCountry"
  | "eventLatitude"
  | "eventLongitude"
  | "eventTimeZone"
  | "gpsRace"
  | "gpsWindowStart"
  | "gpsWindowEnd"
  | "swimKML"
  | "bikeKML"
  | "runKML"
  | "courseKML"
  | "swimDistanceMiles"
  | "bikeDistanceMiles"
  | "runDistanceMiles"
  | "courseDistanceMiles"
  | "swimDistanceKilometers"
  | "bikeDistanceKilometers"
  | "runDistanceKilometers"
  | "courseDistanceKilometers";

export const UNIVERSAL_FIELD_ROLES: LeagueCompetitionUniversalFieldRole[] = [
  "eventName",
  "eventCode",
  "eventShortName",
  "eventStartDateTime",
  "eventLogo",
  "eventLogoDark",
  "eventTitleSponsor",
  "eventCoverImage",
  "eventVenueName",
  "eventAddress",
  "eventCity",
  "eventRegion",
  "eventCountry",
  "eventLatitude",
  "eventLongitude",
  "eventTimeZone",
  "gpsRace",
  "gpsWindowStart",
  "gpsWindowEnd",
  "swimKML",
  "bikeKML",
  "runKML",
  "courseKML",
  "swimDistanceMiles",
  "bikeDistanceMiles",
  "runDistanceMiles",
  "courseDistanceMiles",
  "swimDistanceKilometers",
  "bikeDistanceKilometers",
  "runDistanceKilometers",
  "courseDistanceKilometers",
];

export const UNIVERSAL_ROLE_FIELD_IDS: Record<LeagueCompetitionUniversalFieldRole, string> = {
  eventName: "event-name",
  eventCode: "event-code",
  eventShortName: "event-short-name",
  eventStartDateTime: "event-start-date-time",
  eventLogo: "event-logo",
  eventLogoDark: "event-logo-dark",
  eventTitleSponsor: "event-title-sponsor",
  eventCoverImage: "event-cover-image",
  eventVenueName: "event-venue-name",
  eventAddress: "event-address",
  eventCity: "event-city",
  eventRegion: "event-region",
  eventCountry: "event-country",
  eventLatitude: "event-latitude",
  eventLongitude: "event-longitude",
  eventTimeZone: "event-timezone",
  gpsRace: "gps-race",
  gpsWindowStart: "gps-window-start",
  gpsWindowEnd: "gps-window-end",
  swimKML: "swim-kml",
  bikeKML: "bike-kml",
  runKML: "run-kml",
  courseKML: "course-kml",
  swimDistanceMiles: "swim-distance-miles",
  bikeDistanceMiles: "bike-distance-miles",
  runDistanceMiles: "run-distance-miles",
  courseDistanceMiles: "course-distance-miles",
  swimDistanceKilometers: "swim-distance-kilometers",
  bikeDistanceKilometers: "bike-distance-kilometers",
  runDistanceKilometers: "run-distance-kilometers",
  courseDistanceKilometers: "course-distance-kilometers",
};

export const UNIVERSAL_ROLE_LABELS: Record<LeagueCompetitionUniversalFieldRole, string> = {
  eventName: "Event name",
  eventCode: "Event code",
  eventShortName: "Short name",
  eventStartDateTime: "Start date & time",
  eventLogo: "Logo",
  eventLogoDark: "Logo (dark mode)",
  eventTitleSponsor: "Title sponsor",
  eventCoverImage: "Cover image",
  eventVenueName: "Venue name",
  eventAddress: "Address",
  eventCity: "City",
  eventRegion: "State/Region",
  eventCountry: "Country",
  eventLatitude: "Latitude",
  eventLongitude: "Longitude",
  eventTimeZone: "Event timezone",
  gpsRace: "GPS Race",
  gpsWindowStart: "GPS window start",
  gpsWindowEnd: "GPS window end",
  swimKML: "Swim KML",
  bikeKML: "Bike KML",
  runKML: "Run KML",
  courseKML: "Course KML",
  swimDistanceMiles: "Swim distance (miles)",
  bikeDistanceMiles: "Bike distance (miles)",
  runDistanceMiles: "Run distance (miles)",
  courseDistanceMiles: "Course distance (miles)",
  swimDistanceKilometers: "Swim distance (kilometers)",
  bikeDistanceKilometers: "Bike distance (kilometers)",
  runDistanceKilometers: "Run distance (kilometers)",
  courseDistanceKilometers: "Course distance (kilometers)",
};

const ROLE_SUGGESTED_TYPES: Partial<Record<LeagueCompetitionUniversalFieldRole, LeagueAthleteFieldType>> = {
  eventStartDateTime: "dateTime",
  gpsWindowStart: "dateTime",
  gpsWindowEnd: "dateTime",
  gpsRace: "boolean",
  eventLogo: "photo",
  eventLogoDark: "photo",
  eventCoverImage: "photo",
  eventLatitude: "decimal",
  eventLongitude: "decimal",
  swimDistanceMiles: "decimal",
  bikeDistanceMiles: "decimal",
  runDistanceMiles: "decimal",
  courseDistanceMiles: "decimal",
  swimDistanceKilometers: "decimal",
  bikeDistanceKilometers: "decimal",
  runDistanceKilometers: "decimal",
  courseDistanceKilometers: "decimal",
  swimKML: "url",
  bikeKML: "url",
  runKML: "url",
  courseKML: "url",
};

export function suggestedTypeForRole(role: LeagueCompetitionUniversalFieldRole): LeagueAthleteFieldType {
  return ROLE_SUGGESTED_TYPES[role] ?? "text";
}

export type LeagueAthleteUniversalFieldRole =
  | "participant-id"
  | "full-name"
  | "short-name"
  | "first-name"
  | "last-name"
  | "broadcast-name"
  | "division"
  | "competition-category"
  | "country-name"
  | "country-code"
  | "country-code-system"
  | "flag-url"
  | "headshot-url"
  | "age"
  | "date-of-birth"
  | "is-racing-today"
  | "bib"
  | "uniform-marker"
  | "uniform-marker-hex"
  | "is-notable"
  | "notable-sort-key"
  | "leaderboard-select"
  | "last-updated-at";

export const UNIVERSAL_ATHLETE_FIELD_ROLES: LeagueAthleteUniversalFieldRole[] = [
  "participant-id",
  "full-name",
  "short-name",
  "first-name",
  "last-name",
  "broadcast-name",
  "division",
  "competition-category",
  "country-name",
  "country-code",
  "country-code-system",
  "flag-url",
  "headshot-url",
  "age",
  "date-of-birth",
  "is-racing-today",
  "bib",
  "uniform-marker",
  "uniform-marker-hex",
  "is-notable",
  "notable-sort-key",
  "leaderboard-select",
  "last-updated-at",
];

export const UNIVERSAL_ATHLETE_ROLE_FIELD_IDS: Record<LeagueAthleteUniversalFieldRole, string> = {
  "participant-id": "participant-id",
  "full-name": "full-name",
  "short-name": "short-name",
  "first-name": "first-name",
  "last-name": "last-name",
  "broadcast-name": "broadcast-name",
  division: "division",
  "competition-category": "competition-category",
  "country-name": "country-name",
  "country-code": "country-code",
  "country-code-system": "country-code-system",
  "flag-url": "flag-url",
  "headshot-url": "headshot-url",
  age: "age",
  "date-of-birth": "date-of-birth",
  "is-racing-today": "is-racing-today",
  bib: "bib",
  "uniform-marker": "uniform-marker",
  "uniform-marker-hex": "uniform-marker-hex",
  "is-notable": "is-notable",
  "notable-sort-key": "notable-sort-key",
  "leaderboard-select": "leaderboard-select",
  "last-updated-at": "last-updated-at",
};

export const UNIVERSAL_ATHLETE_ROLE_LABELS: Record<LeagueAthleteUniversalFieldRole, string> = {
  "participant-id": "Participant ID",
  "full-name": "Full name",
  "short-name": "Short name",
  "first-name": "First name",
  "last-name": "Last name",
  "broadcast-name": "Broadcast name",
  division: "Division",
  "competition-category": "Competition category",
  "country-name": "Country name",
  "country-code": "Country code",
  "country-code-system": "Country code system",
  "flag-url": "Flag URL",
  "headshot-url": "Headshot URL",
  age: "Age",
  "date-of-birth": "Date of birth",
  "is-racing-today": "Racing today",
  bib: "Bib",
  "uniform-marker": "Uniform marker",
  "uniform-marker-hex": "Uniform marker hex",
  "is-notable": "Notable",
  "notable-sort-key": "Notable sort key",
  "leaderboard-select": "Leaderboard select",
  "last-updated-at": "Last updated at",
};

export const UNIVERSAL_ATHLETE_ROLE_SUGGESTED_LABELS: Record<LeagueAthleteUniversalFieldRole, string> = {
  "participant-id": "Participant ID",
  "full-name": "Full Name",
  "short-name": "Short Name",
  "first-name": "First Name",
  "last-name": "Last Name",
  "broadcast-name": "Broadcast Name",
  division: "Division",
  "competition-category": "Competition Category",
  "country-name": "Country Name",
  "country-code": "Country Code",
  "country-code-system": "Country Code System",
  "flag-url": "Flag URL",
  "headshot-url": "Headshot URL",
  age: "Age",
  "date-of-birth": "Date of Birth",
  "is-racing-today": "Is Racing Today",
  bib: "Bib",
  "uniform-marker": "Uniform Marker",
  "uniform-marker-hex": "Uniform Marker Hex",
  "is-notable": "Is Notable",
  "notable-sort-key": "Notable Sort Key",
  "leaderboard-select": "Leaderboard Select",
  "last-updated-at": "Last Updated At",
};

export const UNIVERSAL_ATHLETE_ROLE_HELP_TEXT: Record<LeagueAthleteUniversalFieldRole, string> = {
  "participant-id": "Stable participant identifier from the source system or league data.",
  "full-name": "Primary full display name for the participant.",
  "short-name": "Compact graphics name, such as A. Kolk.",
  "first-name": "Participant given name.",
  "last-name": "Participant family name or surname.",
  "broadcast-name": "Optional broadcast override name.",
  division: "Event division, class, or category code.",
  "competition-category": "Broad competitive classification for the participant.",
  "country-name": "Represented country name.",
  "country-code": "Represented country code derived from the selected country when available.",
  "country-code-system": "Code standard used by the country code.",
  "flag-url": "Flag image URL derived from the selected country when available.",
  "headshot-url": "Participant headshot image URL or storage path.",
  age: "Participant age when known.",
  "date-of-birth": "Participant date of birth.",
  "is-racing-today": "Whether the participant is associated with the current live event day.",
  bib: "Bib, race number, roster number, lane number, or equivalent event-day identifier.",
  "uniform-marker": "Visible event-day marker such as cap color, jersey color, or helmet color.",
  "uniform-marker-hex": "Hex color value for the uniform marker.",
  "is-notable": "Whether the participant is manually highlighted for broadcast, graphics, or sorting.",
  "notable-sort-key": "Manual sort key for notable participants.",
  "leaderboard-select": "Marker used to select, pin, group, or filter participants for leaderboard graphics.",
  "last-updated-at": "Timestamp for the most recent update to this participant data.",
};

export const UNIVERSAL_ATHLETE_ROLE_OPTIONS: Partial<Record<LeagueAthleteUniversalFieldRole, LeagueAthleteFieldOption[]>> = {
  "competition-category": [
    { id: "professional", label: "Professional" },
    { id: "elite", label: "Elite" },
    { id: "age-group", label: "Age Group" },
    { id: "amateur", label: "Amateur" },
    { id: "para", label: "Para" },
    { id: "junior", label: "Junior" },
    { id: "collegiate", label: "Collegiate" },
    { id: "team", label: "Team" },
    { id: "relay", label: "Relay" },
    { id: "open", label: "Open" },
    { id: "---", label: "---" },
  ],
  "country-code-system": [
    { id: "IOC", label: "IOC" },
    { id: "ISO-3166-1-alpha-2", label: "ISO-3166-1 alpha-2" },
    { id: "ISO-3166-1-alpha-3", label: "ISO-3166-1 alpha-3" },
    { id: "custom", label: "Custom" },
    { id: "---", label: "---" },
  ],
};

const ATHLETE_ROLE_SUGGESTED_TYPES: Partial<Record<LeagueAthleteUniversalFieldRole, LeagueAthleteFieldType>> = {
  age: "integer",
  "date-of-birth": "date",
  "last-updated-at": "dateTime",
  "is-racing-today": "boolean",
  "is-notable": "boolean",
  "competition-category": "singleSelect",
  "country-code-system": "singleSelect",
  "flag-url": "photo",
  "headshot-url": "photo",
};

export function suggestedTypeForAthleteRole(role: LeagueAthleteUniversalFieldRole): LeagueAthleteFieldType {
  return ATHLETE_ROLE_SUGGESTED_TYPES[role] ?? "text";
}

export interface LeagueAthleteFieldDefinition {
  id: string;
  label: string;
  type: LeagueAthleteFieldType;
  privacyScope: LeagueAthleteFieldPrivacyScope;
  isRequired: boolean;
  helpText?: string;
  example?: string;
  options: LeagueAthleteFieldOption[];
  validation: LeagueAthleteFieldValidation;
  sortOrder: number;
  isArchived: boolean;
  universalCompetitionRole?: LeagueCompetitionUniversalFieldRole;
  universalAthleteRole?: LeagueAthleteUniversalFieldRole;
}

function numberValue(value: unknown): number | undefined {
  return typeof value === "number" ? value : undefined;
}

export function parseFieldDefinition(data: Record<string, unknown>): LeagueAthleteFieldDefinition | null {
  const id = stringValue(data.id);
  const label = stringValue(data.label);
  const type = stringValue(data.type) as LeagueAthleteFieldType | undefined;
  const privacyScope = stringValue(data.privacyScope) as LeagueAthleteFieldPrivacyScope | undefined;
  if (
    !id ||
    label === undefined ||
    !type ||
    !FIELD_TYPES.includes(type) ||
    !privacyScope ||
    !["public", "organization", "league"].includes(privacyScope) ||
    typeof data.isRequired !== "boolean" ||
    typeof data.sortOrder !== "number" ||
    typeof data.isArchived !== "boolean"
  ) {
    return null;
  }

  const validationData = (data.validation ?? {}) as Record<string, unknown>;
  const roleRaw = stringValue(data.universalCompetitionRole);
  const role =
    roleRaw && (UNIVERSAL_FIELD_ROLES as string[]).includes(roleRaw)
      ? (roleRaw as LeagueCompetitionUniversalFieldRole)
      : undefined;
  const athleteRoleRaw = stringValue(data.universalAthleteRole);
  const athleteRole =
    athleteRoleRaw && (UNIVERSAL_ATHLETE_FIELD_ROLES as string[]).includes(athleteRoleRaw)
      ? (athleteRoleRaw as LeagueAthleteUniversalFieldRole)
      : undefined;

  return {
    id,
    label,
    type,
    privacyScope,
    isRequired: data.isRequired,
    helpText: stringValue(data.helpText),
    example: stringValue(data.example),
    options: (Array.isArray(data.options) ? data.options : [])
      .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === "object")
      .flatMap((entry) => {
        const optionId = stringValue(entry.id);
        const optionLabel = stringValue(entry.label);
        return optionId && optionLabel !== undefined ? [{ id: optionId, label: optionLabel }] : [];
      }),
    validation: {
      minNumber: numberValue(validationData.minNumber),
      maxNumber: numberValue(validationData.maxNumber),
      minLength: numberValue(validationData.minLength),
      maxLength: numberValue(validationData.maxLength),
      minSelections: numberValue(validationData.minSelections),
      maxSelections: numberValue(validationData.maxSelections),
      allowedMIMETypes: stringList(validationData.allowedMIMETypes),
    },
    sortOrder: data.sortOrder,
    isArchived: data.isArchived,
    universalCompetitionRole: role,
    universalAthleteRole: athleteRole,
  };
}

export function fieldDefinitionFirestoreData(field: LeagueAthleteFieldDefinition): Record<string, unknown> {
  const validation = compactData({
    minNumber: field.validation.minNumber,
    maxNumber: field.validation.maxNumber,
    minLength: field.validation.minLength,
    maxLength: field.validation.maxLength,
    minSelections: field.validation.minSelections,
    maxSelections: field.validation.maxSelections,
    allowedMIMETypes: field.validation.allowedMIMETypes.length ? field.validation.allowedMIMETypes : undefined,
  });

  return compactData({
    id: field.id,
    label: field.label,
    type: field.type,
    privacyScope: field.privacyScope,
    isRequired: field.isRequired,
    options: field.options.map((option) => ({ id: option.id, label: option.label })),
    validation,
    sortOrder: field.sortOrder,
    isArchived: field.isArchived,
    helpText: field.helpText,
    example: field.example,
    universalCompetitionRole: field.universalCompetitionRole,
    universalAthleteRole: field.universalAthleteRole,
  });
}

export function parseFieldDefinitions(value: unknown): LeagueAthleteFieldDefinition[] {
  return (Array.isArray(value) ? value : [])
    .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === "object")
    .map(parseFieldDefinition)
    .filter((field): field is LeagueAthleteFieldDefinition => field !== null);
}

export function activeFields(fields: LeagueAthleteFieldDefinition[]): LeagueAthleteFieldDefinition[] {
  return fields.filter((field) => !field.isArchived).sort((a, b) => a.sortOrder - b.sortOrder);
}

function uniqueFieldID(base: string, existingFieldIDs: Set<string>): string {
  let id = base;
  let suffix = 2;
  while (existingFieldIDs.has(id)) {
    id = `${base}-${suffix}`;
    suffix += 1;
  }
  return id;
}

/** Mirrors LeagueCompetitionUniversalFieldRole.makeField(sortOrder:existingFieldIDs:). */
export function makeFieldForRole(
  role: LeagueCompetitionUniversalFieldRole,
  sortOrder: number,
  existingFieldIDs: Set<string>,
): LeagueAthleteFieldDefinition {
  const type = suggestedTypeForRole(role);
  let minNumber: number | undefined;
  let maxNumber: number | undefined;
  if (role === "eventLatitude") {
    minNumber = -90;
    maxNumber = 90;
  } else if (role === "eventLongitude") {
    minNumber = -180;
    maxNumber = 180;
  } else if (role.includes("Distance")) {
    minNumber = 0;
  }

  const base = UNIVERSAL_ROLE_FIELD_IDS[role];
  const id = uniqueFieldID(base, existingFieldIDs);

  return {
    id,
    label: UNIVERSAL_ROLE_LABELS[role],
    type,
    privacyScope: "public",
    isRequired: role === "eventName" || role === "eventStartDateTime",
    helpText: undefined,
    options: [],
    validation: {
      minNumber,
      maxNumber,
      allowedMIMETypes: type === "photo" ? ALLOWED_PHOTO_MIME_TYPES : [],
    },
    sortOrder,
    isArchived: false,
    universalCompetitionRole: role,
  };
}

/** Mirrors LeagueAthleteUniversalFieldRole.makeField(sortOrder:existingFieldIDs:). */
export function makeFieldForAthleteRole(
  role: LeagueAthleteUniversalFieldRole,
  sortOrder: number,
  existingFieldIDs: Set<string>,
): LeagueAthleteFieldDefinition {
  const type = suggestedTypeForAthleteRole(role);
  const validation: LeagueAthleteFieldValidation = {
    allowedMIMETypes: type === "photo" ? ALLOWED_PHOTO_MIME_TYPES : [],
  };
  if (role === "age") {
    validation.minNumber = 0;
    validation.maxNumber = 120;
  }
  if (role === "uniform-marker-hex") {
    validation.minLength = 7;
    validation.maxLength = 7;
  }

  return {
    id: uniqueFieldID(UNIVERSAL_ATHLETE_ROLE_FIELD_IDS[role], existingFieldIDs),
    label: UNIVERSAL_ATHLETE_ROLE_SUGGESTED_LABELS[role],
    type,
    privacyScope: "public",
    isRequired: false,
    helpText: UNIVERSAL_ATHLETE_ROLE_HELP_TEXT[role],
    options: UNIVERSAL_ATHLETE_ROLE_OPTIONS[role] ?? [],
    validation,
    sortOrder,
    isArchived: false,
    universalAthleteRole: role,
  };
}

export function sbotSlug(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join("-");
}
